"use client";

import { useMemo } from "react";
import { useRouter } from "next/navigation";
import { useGlobalModalStack } from "@wtc/ui";
import { toast } from "sonner";
import type {
  DetailActionContext,
  BaseEntity,
} from "@/components/shared/UniversalDetailPage/types/detailPage";
import type {
  EntityHooks,
  SimpleTableConfig,
} from "@/components/shared/UniversalCRMTable/types/shared";
import type { EntityWithRelations } from "@/lib/types/table/entity-relation-types";

// ============================================================================
// TYPES
// ============================================================================

export interface TableRowActionContextParams<T extends BaseEntity> {
  item: T | null | undefined;
  hooks: EntityHooks<T>;
  refetch: () => void | Promise<void>;
  updateMutation: ReturnType<EntityHooks<T>["useUpdate"]>;
  deleteMutation: ReturnType<EntityHooks<T>["useDelete"]>;
  entityName: string;
  config?: SimpleTableConfig<T>;
  onDeleteComplete?: () => void;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to build action context for table rows
 * Similar to useBuildActionContext but adapted for table row operations
 * Returns the same DetailActionContext interface for consistency
 */
export function useTableRowActionContext<T extends BaseEntity>({
  item,
  hooks,
  refetch,
  updateMutation,
  deleteMutation,
  entityName,
  config,
  onDeleteComplete,
}: TableRowActionContextParams<T>): DetailActionContext<T> | null {
  const router = useRouter();
  const { openModal, closeModal } = useGlobalModalStack();

  return useMemo(() => {
    if (!item) return null;

    // Extract entity IDs from the item
    const typed = item as EntityWithRelations<T>;
    const entityIds = {
      contactId:
        typed.contact_id ||
        typed.id ||
        (entityName === "contacts" ? item.id : undefined),
      ticketId: typed.ticket_id || (entityName === "tickets" ? item.id : undefined),
      companyId: typed.company_id || (entityName === "companies" ? item.id : undefined),
      employeeId: typed.employee_id || (entityName === "employees" ? item.id : undefined),
      userId: typed.user_id || (entityName === "users" ? item.id : undefined),
      taskId: typed.task_id || (entityName === "tasks" ? item.id : undefined),
      dealId: typed.deal_id || (entityName === "deals" ? item.id : undefined),
    };

    return {
      pageConfig: {
        entityName,
        config: (config ?? {}) as any,
        hooks,
      },
      item,
      hooks,
      entityName,
      entityIds,
      onRefresh: () => refetch(),
      onNavigate: (href: string) => router.push(href),
      onToast: (message: string, type: "success" | "error" | "info" | "warning" = "info") => {
        toast[type](message);
      },
      onOpenModal: (modalId: string, data?: Record<string, any>) => {
        // If data is already provided, use it
        if (data) {
          openModal(modalId, data);
        } else {
          // Otherwise, build structured data from the item based on modal ID
          const structuredData: Record<string, any> = { item };

          // Add common IDs based on entity type
          if (item.contact_id) structuredData.contactId = item.contact_id;
          if (item.ticket_id) structuredData.ticketId = item.ticket_id;
          if (item.company_id) structuredData.companyId = item.company_id;
          if (item.employee_id) structuredData.employeeId = item.employee_id;
          if (item.user_id) structuredData.userId = item.user_id;
          if (item.task_id) structuredData.taskId = item.task_id;

          // For contacts, use the item ID as contactId
          if (entityName === "contacts" && item.id) {
            structuredData.contactId = item.id;
          }
          // For tickets, use the item ID as ticketId
          if (entityName === "tickets" && item.id) {
            structuredData.ticketId = item.id;
          }
          // For deals, use the item ID as dealId
          if (entityName === "deals" && item.id) {
            structuredData.dealId = item.id;
          }

          openModal(modalId, structuredData);
        }
      },
      openModalTypeSafe: (modalId: string, data?: Record<string, any>) => {
        // Type-safe version - delegates to onOpenModal for now
        openModal(modalId, data);
      },
      onCloseModal: (modalId: string) => {
        closeModal(modalId);
      },
      onUpdateItem: async (updates: Partial<T>) => {
        if (!item) throw new Error("No item to update");

        // Use EntityHooks interface - always use mutateAsync
        await updateMutation.mutateAsync({
          ...updates,
          id: item.id,
        });

        await refetch();
      },
      onDeleteItem: async () => {
        if (!item) throw new Error("No item to delete");

        // Use EntityHooks interface - always use mutateAsync
        await deleteMutation.mutateAsync(item.id);

        // Call the callback if provided
        if (onDeleteComplete) {
          onDeleteComplete();
        } else {
          // Default behavior: navigate back to list
          router.push(`/${entityName}`);
        }
      },
    } as DetailActionContext<T>;
  }, [
    item,
    hooks,
    refetch,
    router,
    entityName,
    openModal,
    closeModal,
    updateMutation,
    deleteMutation,
    onDeleteComplete,
  ]);
}
